import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../../utils/currencyFormatter';
import { useCurrency } from '../../context/CurrencyContext';

const CustomerDashboardPage = () => {
    const { user, logout, authToken } = useAuth();
    const navigate = useNavigate();
    const { currency } = useCurrency();

    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'addresses'

    // Order State
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [ordersError, setOrdersError] = useState('');

    // Address State
    const [addresses, setAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(true);
    const [addressesError, setAddressesError] = useState('');
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        street: '',
        address2: '',
        country: 'Sri Lanka',
        city: '',
        zipCode: '',
        isDefault: false
    });

    useEffect(() => {
        if (!authToken) {
            navigate('/signin');
            return;
        }
        if (activeTab === 'orders') {
            fetchOrders();
        } else if (activeTab === 'addresses') {
            fetchAddresses();
        }
    }, [authToken, navigate, activeTab]);

    const handleLogout = () => {
        logout();
        navigate('/signin');
    };

    // --- ORDERS LOGIC ---
    const fetchOrders = async () => {
        setLoadingOrders(true);
        try {
            const config = { headers: { Authorization: `Bearer ${authToken}` } };
            const response = await axios.get('/api/users/my-orders', config);
            setOrders(response.data);
            setOrdersError('');
        } catch (err) {
            if (err.response && err.response.status === 404) {
                setOrders([]);
            } else {
                setOrdersError(err.response?.data?.message || "Failed to fetch orders.");
            }
        } finally {
            setLoadingOrders(false);
        }
    };

    // --- ADDRESSES LOGIC ---
    const fetchAddresses = async () => {
        setLoadingAddresses(true);
        try {
            const config = { headers: { Authorization: `Bearer ${authToken}` } };
            const response = await axios.get('/api/users/addresses', config);
            setAddresses(response.data);
            setAddressesError('');
        } catch (err) {
            setAddressesError(err.response?.data?.message || "Failed to fetch addresses.");
        } finally {
            setLoadingAddresses(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            phone: '',
            street: '',
            address2: '',
            country: 'Sri Lanka',
            city: '',
            zipCode: '',
            isDefault: false
        });
        setShowAddressForm(false);
        setEditingAddressId(null);
    };

    const handleAddOrUpdateAddress = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${authToken}` } };

            // Note: Add any custom mapping here to match backend schema.
            // Backend User.js addresses: type, firstName, lastName, street, city, state, zipCode, country, isDefault

            if (editingAddressId) {
                await axios.put(`/api/users/addresses/${editingAddressId}`, formData, config);
            } else {
                await axios.post('/api/users/addresses', formData, config);
            }

            fetchAddresses();
            resetForm();
        } catch (err) {
            console.error("Save address error:", err);
            alert(err.response?.data?.message || "Failed to save address.");
        }
    };

    const handleEditAddress = (address) => {
        setFormData({
            firstName: address.firstName || '',
            lastName: address.lastName || '',
            phone: address.phone || '',
            street: address.street || '',
            address2: address.address2 || '',
            country: address.country || 'Sri Lanka',
            city: address.city || '',
            zipCode: address.zipCode || '',
            isDefault: address.isDefault || false
        });
        setEditingAddressId(address._id);
        setShowAddressForm(true);
    };

    const handleDeleteAddress = async (addressId) => {
        if (window.confirm("Are you sure you want to delete this address?")) {
            try {
                const config = { headers: { Authorization: `Bearer ${authToken}` } };
                await axios.delete(`/api/users/addresses/${addressId}`, config);
                fetchAddresses();
            } catch (err) {
                console.error("Delete address error:", err);
                alert(err.response?.data?.message || "Failed to delete address.");
            }
        }
    };

    if (!user) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className="bg-white min-h-screen text-gray-800 font-sans">
            <div className="max-w-5xl mx-auto p-4 sm:p-8">

                {/* Header Top Section */}
                <div className="flex justify-between items-center mb-10 border-b pb-4">
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight" style={{ color: '#0d1b2a', letterSpacing: '-0.02em' }}>My account</h1>
                    <div className="flex items-center text-sm gap-2">
                        <span className="text-gray-600">Welcome back, {user.name}!</span>
                        <button onClick={handleLogout} className="font-semibold underline hover:text-gray-600 transition">
                            Log out
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-6 mb-8">
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${activeTab === 'orders' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                    >
                        My orders
                    </button>
                    <button
                        onClick={() => setActiveTab('addresses')}
                        className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${activeTab === 'addresses' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                    >
                        My addresses
                    </button>
                </div>

                {/* --- ORDERS TAB --- */}
                {activeTab === 'orders' && (
                    <div className="animate-fade-in">
                        <h2 className="text-lg font-medium mb-6">My orders ({orders.length})</h2>

                        {loadingOrders ? (
                            <p className="text-gray-500">Loading orders...</p>
                        ) : ordersError ? (
                            <p className="text-red-500">{ordersError}</p>
                        ) : orders.length === 0 ? (
                            <p className="text-gray-600 text-sm">You haven't placed any orders yet.</p>
                        ) : (
                            <div className="space-y-6">
                                {/* Order List Render Placeholder */}
                                {/* Will map logic from OrderHistoryPage.js but styling modified to match simple approach */}
                                {orders.map(order => (
                                    <div key={order._id} className="border rounded-md p-4 flex justify-between items-center">
                                        <div>
                                            <p className="font-semibold text-sm">Order #{order._id.substring(0, 8)}</p>
                                            <p className="text-xs text-gray-500">Placed on {new Date(order.createdAt).toLocaleDateString()}</p>
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{formatCurrency(order.totalPrice, currency)}</p>
                                            <p className="text-xs text-gray-500 capitalize">{order.status}</p>
                                        </div>
                                        <Link to={`/orders/${order._id}`} className="text-sm underline hover:text-gray-600">
                                            View Details
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* --- ADDRESSES TAB --- */}
                {activeTab === 'addresses' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-medium text-gray-800">My addresses ({addresses.length})</h2>
                            {!showAddressForm && (
                                <button
                                    onClick={() => setShowAddressForm(true)}
                                    className="border border-gray-300 px-4 py-2 text-sm font-medium rounded hover:bg-gray-50 transition"
                                >
                                    Add a new address
                                </button>
                            )}
                        </div>

                        {/* Add / Edit Form */}
                        {showAddressForm && (
                            <div className="mb-10 bg-white">
                                <h3 className="text-md font-medium text-gray-800 mb-6 border-b pb-2">
                                    {editingAddressId ? 'Edit address' : 'Add a new address'}
                                </h3>
                                <form onSubmit={handleAddOrUpdateAddress} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">First name <span className="text-red-500">*</span></label>
                                            <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="First name" />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">Last name</label>
                                            <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="Last name" />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">Phone</label>
                                            <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="Phone" />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">Address 1 <span className="text-red-500">*</span></label>
                                            <input type="text" name="street" value={formData.street} onChange={handleInputChange} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="Address 1" />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">Address 2</label>
                                            <input type="text" name="address2" value={formData.address2} onChange={handleInputChange} className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="Address 2" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-gray-700 mb-1 font-medium">Country/region <span className="text-red-500">*</span></label>
                                            <select name="country" value={formData.country} onChange={handleInputChange} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black">
                                                <option value="">Select country...</option>
                                                <option value="Afghanistan">Afghanistan</option>
                                                <option value="Albania">Albania</option>
                                                <option value="Algeria">Algeria</option>
                                                <option value="Argentina">Argentina</option>
                                                <option value="Armenia">Armenia</option>
                                                <option value="Australia">Australia</option>
                                                <option value="Austria">Austria</option>
                                                <option value="Azerbaijan">Azerbaijan</option>
                                                <option value="Bahrain">Bahrain</option>
                                                <option value="Bangladesh">Bangladesh</option>
                                                <option value="Belarus">Belarus</option>
                                                <option value="Belgium">Belgium</option>
                                                <option value="Bolivia">Bolivia</option>
                                                <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                                                <option value="Brazil">Brazil</option>
                                                <option value="Bulgaria">Bulgaria</option>
                                                <option value="Cambodia">Cambodia</option>
                                                <option value="Canada">Canada</option>
                                                <option value="Chile">Chile</option>
                                                <option value="China">China</option>
                                                <option value="Colombia">Colombia</option>
                                                <option value="Croatia">Croatia</option>
                                                <option value="Cuba">Cuba</option>
                                                <option value="Cyprus">Cyprus</option>
                                                <option value="Czech Republic">Czech Republic</option>
                                                <option value="Denmark">Denmark</option>
                                                <option value="Ecuador">Ecuador</option>
                                                <option value="Egypt">Egypt</option>
                                                <option value="Estonia">Estonia</option>
                                                <option value="Ethiopia">Ethiopia</option>
                                                <option value="Finland">Finland</option>
                                                <option value="France">France</option>
                                                <option value="Georgia">Georgia</option>
                                                <option value="Germany">Germany</option>
                                                <option value="Ghana">Ghana</option>
                                                <option value="Greece">Greece</option>
                                                <option value="Hungary">Hungary</option>
                                                <option value="Iceland">Iceland</option>
                                                <option value="India">India</option>
                                                <option value="Indonesia">Indonesia</option>
                                                <option value="Iran">Iran</option>
                                                <option value="Iraq">Iraq</option>
                                                <option value="Ireland">Ireland</option>
                                                <option value="Israel">Israel</option>
                                                <option value="Italy">Italy</option>
                                                <option value="Jamaica">Jamaica</option>
                                                <option value="Japan">Japan</option>
                                                <option value="Jordan">Jordan</option>
                                                <option value="Kazakhstan">Kazakhstan</option>
                                                <option value="Kenya">Kenya</option>
                                                <option value="Kuwait">Kuwait</option>
                                                <option value="Kyrgyzstan">Kyrgyzstan</option>
                                                <option value="Laos">Laos</option>
                                                <option value="Latvia">Latvia</option>
                                                <option value="Lebanon">Lebanon</option>
                                                <option value="Libya">Libya</option>
                                                <option value="Lithuania">Lithuania</option>
                                                <option value="Luxembourg">Luxembourg</option>
                                                <option value="Malaysia">Malaysia</option>
                                                <option value="Maldives">Maldives</option>
                                                <option value="Malta">Malta</option>
                                                <option value="Mexico">Mexico</option>
                                                <option value="Moldova">Moldova</option>
                                                <option value="Mongolia">Mongolia</option>
                                                <option value="Morocco">Morocco</option>
                                                <option value="Myanmar">Myanmar</option>
                                                <option value="Nepal">Nepal</option>
                                                <option value="Netherlands">Netherlands</option>
                                                <option value="New Zealand">New Zealand</option>
                                                <option value="Nigeria">Nigeria</option>
                                                <option value="North Korea">North Korea</option>
                                                <option value="Norway">Norway</option>
                                                <option value="Oman">Oman</option>
                                                <option value="Pakistan">Pakistan</option>
                                                <option value="Palestine">Palestine</option>
                                                <option value="Panama">Panama</option>
                                                <option value="Peru">Peru</option>
                                                <option value="Philippines">Philippines</option>
                                                <option value="Poland">Poland</option>
                                                <option value="Portugal">Portugal</option>
                                                <option value="Qatar">Qatar</option>
                                                <option value="Romania">Romania</option>
                                                <option value="Russia">Russia</option>
                                                <option value="Saudi Arabia">Saudi Arabia</option>
                                                <option value="Serbia">Serbia</option>
                                                <option value="Singapore">Singapore</option>
                                                <option value="Slovakia">Slovakia</option>
                                                <option value="Slovenia">Slovenia</option>
                                                <option value="South Africa">South Africa</option>
                                                <option value="South Korea">South Korea</option>
                                                <option value="Spain">Spain</option>
                                                <option value="Sri Lanka">Sri Lanka</option>
                                                <option value="Sudan">Sudan</option>
                                                <option value="Sweden">Sweden</option>
                                                <option value="Switzerland">Switzerland</option>
                                                <option value="Syria">Syria</option>
                                                <option value="Taiwan">Taiwan</option>
                                                <option value="Tajikistan">Tajikistan</option>
                                                <option value="Tanzania">Tanzania</option>
                                                <option value="Thailand">Thailand</option>
                                                <option value="Tunisia">Tunisia</option>
                                                <option value="Turkey">Turkey</option>
                                                <option value="Turkmenistan">Turkmenistan</option>
                                                <option value="Uganda">Uganda</option>
                                                <option value="Ukraine">Ukraine</option>
                                                <option value="United Arab Emirates">United Arab Emirates</option>
                                                <option value="United Kingdom">United Kingdom</option>
                                                <option value="United States">United States</option>
                                                <option value="Uruguay">Uruguay</option>
                                                <option value="Uzbekistan">Uzbekistan</option>
                                                <option value="Venezuela">Venezuela</option>
                                                <option value="Vietnam">Vietnam</option>
                                                <option value="Yemen">Yemen</option>
                                                <option value="Zimbabwe">Zimbabwe</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">City <span className="text-red-500">*</span></label>
                                            <input type="text" name="city" value={formData.city} onChange={handleInputChange} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="City" />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1 font-medium">ZIP code <span className="text-red-500">*</span></label>
                                            <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-black" placeholder="ZIP code" />
                                        </div>
                                    </div>

                                    <div className="flex items-center mt-3">
                                        <input type="checkbox" id="isDefault" name="isDefault" checked={formData.isDefault} onChange={handleInputChange} className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black accent-black" />
                                        <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700 font-medium">Set as default address</label>
                                    </div>

                                    <div className="flex justify-end gap-3 mt-6">
                                        <button type="button" onClick={resetForm} className="px-5 py-2 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition">Cancel</button>
                                        <button type="submit" className="px-5 py-2 bg-black text-white rounded text-sm font-medium hover:bg-gray-800 transition">
                                            {editingAddressId ? 'Update address' : 'Add address'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* List of Addresses */}
                        {loadingAddresses ? (
                            <p className="text-gray-500 mt-4">Loading addresses...</p>
                        ) : addressesError ? (
                            <p className="text-red-500 mt-4">{addressesError}</p>
                        ) : (
                            <div className="space-y-6">
                                {addresses.map(address => (
                                    <div key={address._id} className="border-t border-gray-200 py-6 flex justify-between">
                                        <div className="text-sm text-gray-600 w-1/4">
                                            {address.isDefault && <span className="inline-block border border-gray-300 rounded-full px-3 py-1 text-xs">Default</span>}
                                        </div>

                                        <div className="text-sm text-gray-700 flex-1">
                                            <p className="font-medium">{address.firstName} {address.lastName}</p>
                                            <p>{address.street}</p>
                                            {address.address2 && <p>{address.address2}</p>}
                                            <p>{address.city}, {address.zipCode}</p>
                                            <p>{address.country}</p>
                                            {address.phone && <p>{address.phone}</p>}
                                        </div>

                                        <div className="flex items-start gap-4 text-sm font-semibold underline">
                                            <button onClick={() => handleEditAddress(address)} className="hover:text-gray-600 transition">Edit</button>
                                            <button onClick={() => handleDeleteAddress(address._id)} className="hover:text-gray-600 transition">Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Adding fade-in animation */}
            <style>{`
                .animate-fade-in {
                    animation: fadeIn 0.3s ease-in-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default CustomerDashboardPage;